import { dateFormat, OrganizationReports } from '@alice-financial/alice-sdk'
import { API_ORIGIN } from '@alice-financial/api'
import {
  Box,
  Button,
  DateInputController,
  FormLabel,
  Grid,
  RadioButton,
  RadioGroup,
  Typography,
} from '@alice-financial/pretext-ui'
import DownloadIcon from '@mui/icons-material/Download'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { useOrgId } from '../../orgDashboard/useOrgId'
import { PageBody } from '../../routes/PageBody'
import { useOrg } from '../organization/useOrg'

type ReportType = Exclude<keyof OrganizationReports, '__typename'>
const DISPLAYED_REPORT_TYPES: ReportType[] = [
  'invoices',
  'financial',
  'aliceCardReconciliation',
  'payPeriods',
]

interface FormValues {
  startDate: string
  endDate: string
  reportType: ReportType | null
}

interface ReportTypeSelectorProps {
  selectedType: ReportType | null
  onSelect: (type: ReportType) => void
  reports: OrganizationReports
}

const ReportTypeSelector = ({
  selectedType,
  onSelect,
  reports: { __typename, ...reports },
}: ReportTypeSelectorProps) => {
  return (
    <RadioGroup
      value={selectedType || ''}
      onChange={(e) => onSelect(e.target.value as ReportType)}
      aria-label="report type"
    >
      {DISPLAYED_REPORT_TYPES.map((id) => (
        <RadioButton
          key={id}
          value={id}
          sx={{ py: 1 }}
          label={
            <>
              <Typography variant="body2" fontWeight="bold">
                {reports[id].title}
              </Typography>
              <Typography variant="caption" component="p">
                {reports[id].description}
              </Typography>
            </>
          }
        />
      ))}
    </RadioGroup>
  )
}

type DateRangeFormProps = {
  control: ReturnType<typeof useForm<FormValues>>['control']
  selectedType: ReportType | null
}

const DateRangeForm = ({ control, selectedType }: DateRangeFormProps) => {
  const today = React.useMemo(() => dateFormat.isoDate(new Date()), [])
  const earliestDate = React.useMemo(() => dateFormat.isoDate(new Date(2020, 0, 1)), [])

  if (!selectedType) return null

  return (
    <Grid>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormLabel htmlFor="startDate">Start Date</FormLabel>
          <DateInputController name="startDate" required control={control} max={today} min={earliestDate} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormLabel htmlFor="endDate">End Date</FormLabel>
          <DateInputController
            name="endDate"
            required
            control={control}
            rules={{
              required: 'End date is required',
              validate: (value, formValues) => {
                if (!formValues.startDate || !value) return true
                if (value > today) return 'End date must be before today'
                return value >= formValues.startDate || 'End date must be after start date'
              },
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}

const ReportDownload = ({ disabled }: { disabled?: boolean }) => {
  return (
    <Box mt={4}>
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={disabled}
        startIcon={<DownloadIcon />}
      >
        Download Report
      </Button>
    </Box>
  )
}

const handleReportDownload = (values: FormValues, orgId: number, reports: OrganizationReports) => {
  if (!values.reportType) return

  const selectedReport = reports[values.reportType]
  if (!selectedReport) return

  const reportPath = selectedReport.downloadUrl
  const downloadUrl = `${API_ORIGIN}/${reportPath}?start_date=${values.startDate}&end_date=${values.endDate}`
  window.location.href = downloadUrl
}

export const ReportBuilderPage = () => {
  const today = React.useMemo(() => dateFormat.isoDate(new Date()), [])
  const startOfYear = React.useMemo(() => {
    const date = new Date()
    date.setMonth(0)
    date.setDate(1)
    return dateFormat.isoDate(date)
  }, [])
  const orgId = useOrgId()
  const org = useOrg()

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { isValid },
  } = useForm<FormValues>({
    defaultValues: {
      startDate: startOfYear,
      endDate: today,
      reportType: null,
    },
    mode: 'onChange',
  })
  const selectedType = watch('reportType')
  const setSelectedType = (type: ReportType) => {
    setValue('reportType', type)
  }
  if (!org || !org.reports) return null

  return (
    <PageBody maxWidth={1200}>
      <Typography variant="h1" gutterBottom>
        Report Builder
      </Typography>

      <form onSubmit={handleSubmit((values) => handleReportDownload(values, orgId, org.reports))}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <ReportTypeSelector
              selectedType={selectedType}
              onSelect={setSelectedType}
              reports={org.reports}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Grid sx={{ maxWidth: '66%' }}>
              <DateRangeForm control={control} selectedType={selectedType} />
              {selectedType && <ReportDownload disabled={!isValid} />}
            </Grid>
          </Grid>
        </Grid>
      </form>
    </PageBody>
  )
}
